import { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Input, message, Table } from 'antd';
import { CloseOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { config } from './definitions/vendor-share/config';
import { api } from './definitions/vendor-share/api';
import CustomPagination from '../CustomPagination';
import CommonMailComposer from './CommonMailComposer';
import { areValidEmails, parseEmails } from './mailUtils';

const vendorColumns = config.vendorColumns.map((col) => ({
  ...col,
  render: (val) => val || '-',
}));

export default function VendorShareDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedIds = [],
    selectedRecords = [],
    recipients = [],
    subject: initialSubject = '',
    onSend,
    moduleName = '',
    popupAction = {},
  } = location.state ?? {};

  const jobId = selectedIds[0] ?? selectedRecords[0]?._id ?? selectedRecords[0]?.id;

  const [mail, setMail] = useState({ to: recipients.join(', '), cc: '', bcc: '', subject: initialSubject, contents: '', showCc: false, showBcc: false });
  const [detailsLoaded, setDetailsLoaded] = useState(!jobId);

  const [vendorTab, setVendorTab] = useState(config.defaultTab);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendors, setVendors] = useState([]);
  const [myVendorCount, setMyVendorCount] = useState(0);
  const [allVendorCount, setAllVendorCount] = useState(0);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorPageSize, setVendorPageSize] = useState(config.defaultPageSize);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [selectedVendorKeys, setSelectedVendorKeys] = useState([]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setVendorLoading(true);
    const limit = vendorSearch ? 100 : vendorPageSize;
    const offset = vendorSearch ? 0 : (vendorPage - 1) * vendorPageSize;
    api.getVendorList({ jobId, limit, offset, type: vendorTab })
      .then((res) => {
        if (cancelled) return;
        const vendorList = res?.data?.vendorList ?? res?.data ?? [];
        const myCount = res?.data?.myVendorCount ?? 0;
        const allCount = res?.data?.allVendorCount ?? 0;
        setVendors(Array.isArray(vendorList) ? vendorList : []);
        setMyVendorCount(myCount);
        setAllVendorCount(allCount);
        setVendorTotal(vendorTab === 'myVendor' ? myCount : allCount);
      })
      .catch(() => { if (!cancelled) { setVendors([]); setVendorTotal(0); } })
      .finally(() => { if (!cancelled) setVendorLoading(false); });
    return () => { cancelled = true; };
  }, [jobId, vendorTab, vendorPage, vendorPageSize, vendorSearch]);

  useEffect(() => {
    if (!jobId) return;
    api.getMailDetails(jobId).then((data) => setMail((current) => ({
      ...current,
      subject: data.subject || current.subject,
      contents: data.contents || current.contents,
      to: data.toMail?.length ? (Array.isArray(data.toMail) ? data.toMail.join(', ') : String(data.toMail)) : current.to,
      cc: data.ccMail?.length ? (Array.isArray(data.ccMail) ? data.ccMail.join(', ') : String(data.ccMail)) : current.cc,
    }))).catch(() => {}).finally(() => setDetailsLoaded(true));
  }, [jobId]);

  const selectedVendorEmails = useMemo(() => selectedVendorKeys
    .map((key) => vendors.find((vendor) => vendor.vendorId === key)?.vendorEmail ?? '')
    .filter(Boolean), [selectedVendorKeys, vendors]);

  const composerMail = useMemo(() => ({
    ...mail,
    bcc: [...new Set([...parseEmails(mail.bcc), ...selectedVendorEmails])].join(', '),
    showBcc: mail.showBcc || selectedVendorEmails.length > 0,
  }), [mail, selectedVendorEmails]);

  function handleComposerChange(next) {
    const vendorSet = new Set(selectedVendorEmails);
    setMail({ ...next, bcc: parseEmails(next.bcc).filter((email) => !vendorSet.has(email)).join(', ') });
  }

  const canSend = (parseEmails(composerMail.to).length || parseEmails(composerMail.bcc).length)
    && areValidEmails(composerMail.to) && areValidEmails(composerMail.cc) && areValidEmails(composerMail.bcc) && composerMail.subject.trim();
  const tabCount = (key) => key === 'myVendor' ? myVendorCount : allVendorCount;

  function handleClose() { navigate(-1); }

  function handleSend() {
    if (!canSend) return;
    api.submit({
      jobId,
      toMail: parseEmails(composerMail.to),
      ccMail: parseEmails(composerMail.cc),
      bccMail: parseEmails(composerMail.bcc),
      subject: composerMail.subject,
      contents: composerMail.contents,
    }).then((res) => {
      if (res?.status === true) {
        message.success(res?.data || 'Mail sent successfully.');
        if (typeof onSend === 'function') onSend({ ...composerMail, message: composerMail.contents, selectedIds });
        navigate(-1);
      } else {
        message.error(res?.message || res?.data || 'Failed to send mail. Please try again.');
      }
    }).catch((err) => { message.error(err?.message || 'Failed to send mail. Please try again.'); });
  }

  const filteredVendors = useMemo(() => {
    if (!vendorSearch) return vendors;
    const q = vendorSearch.toLowerCase();
    return vendors.filter((v) =>
      (v.vendorName ?? '').toLowerCase().includes(q)
      || (v.vendorEmail ?? '').toLowerCase().includes(q)
      || (v.vendorLocation ?? '').toLowerCase().includes(q),
    );
  }, [vendors, vendorSearch]);

  const footer = (
    <div className="vendor-share__footer">
      <Button onClick={handleClose}>Cancel</Button>
      <Button type="primary" disabled={!canSend} onClick={handleSend}>Send</Button>
    </div>
  );

  return (
    <Drawer
      open
      placement="right"
      onClose={handleClose}
      closable={false}
      rootClassName="vendor-share"
      width="95%"
      footer={footer}
    >
      <div className="vendor-share__body">

        {/* ── Left: Vendor List ── */}
        <div className="vendor-share__left">
          {/* vendor-header: close + title + search (flex row), tabs below */}
          <div className="vendor-share__vendor-header">
            <div className="vendor-share__vendor-header-row">
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                size="small"
                style={{ marginRight: 8 }}
              />
              <span className="dynamic-popup__header-icon-box">
                <TeamOutlined />
              </span>
              <span className="dynamic-popup__header-label">Vendor List</span>
              {/* margin-left:auto in CSS pushes search to the right */}
              <div className="vendor-share__vendor-search">
                <SearchOutlined className="vendor-share__search-icon" />
                <Input
                  variant="borderless"
                  placeholder="Search vendor"
                  value={vendorSearch}
                  onChange={(e) => { setVendorSearch(e.target.value); setVendorPage(1); }}
                  className="vendor-share__search-input"
                />
              </div>
            </div>

            <div className="vendor-share__vendor-tabs">
              {config.vendorTabs.map((tab) => (
                <span
                  key={tab.key}
                  className={`vendor-share__vendor-tab${vendorTab === tab.key ? ' vendor-share__vendor-tab--active' : ''}`}
                  onClick={() => { setVendorTab(tab.key); setVendorPage(1); setSelectedVendorKeys([]); }}
                >
                  {tab.label} ({tabCount(tab.key)})
                </span>
              ))}
            </div>
          </div>

          <div className="vendor-share__vendor-table-wrapper">
            <Table
              rowKey={(record) => record.vendorId}
              columns={vendorColumns}
              dataSource={filteredVendors}
              loading={vendorLoading}
              pagination={false}
              size="small"
              rowSelection={{
                selectedRowKeys: selectedVendorKeys,
                onChange: (keys) => {
                  if (keys.length > config.maxVendorSelect) return;
                  setSelectedVendorKeys(keys);
                },
              }}
              scroll={{ y: 'calc(100vh - 340px)' }}
            />
          </div>

          <div className="vendor-share__vendor-footer">
            <CustomPagination
              current={vendorPage}
              pageSize={vendorPageSize}
              total={vendorTotal}
              onChange={(page) => setVendorPage(page)}
              onPageSizeChange={(size) => setVendorPageSize(size)}
            />
          </div>
        </div>

        {/* ── Right: Share / Compose ── */}
        <div className="vendor-share__right">
          <CommonMailComposer
            value={composerMail}
            onChange={handleComposerChange}
            moduleName={moduleName}
            defaultTemplateId={popupAction.defaultTemplateId ?? location.state?.defaultTemplateId ?? ''}
            ready={detailsLoaded}
            contextRecords={selectedRecords}
            title="Share"
            hint="Select vendors or add email addresses"
            bccHint="Vendor emails are added automatically"
            afterBcc={selectedVendorKeys.length > 0 ? <div className="vendor-share__selected-count">{selectedVendorKeys.length} / {config.maxVendorSelect} Vendors selected</div> : null}
          />
        </div>

      </div>
    </Drawer>
  );
}
